<template>
  <div class="metric-card" :class="{ 'metric-card-clickable': !!clickAction }" @click="handleClick" role="button" :tabindex="clickAction ? 0 : -1" :aria-label="ariaLabel">
    <div class="metric-label">{{ label }}</div>
    <div class="metric-value">
      <span v-if="state === 'loaded'" :title="displayValue">{{ displayValue }}</span>
      <span v-else-if="state === 'loading'" class="metric-skeleton">--</span>
      <span v-else-if="state === 'unavailable'" class="metric-unavailable">Unavailable</span>
      <span v-else-if="state === 'error'" class="metric-error">Error</span>
      <span v-else class="metric-empty">--</span>
    </div>
    <div v-if="state === 'loaded' && unit" class="metric-unit">{{ unit }}</div>
    <div v-if="state === 'loaded' && trendDirection" class="metric-trend" :class="'trend-' + trendDirection">
      <span class="trend-arrow">{{ trendArrow }}</span>
      <span class="trend-label">{{ trendLabel }}</span>
    </div>
    <div v-if="state === 'loaded' && comparisonPeriod" class="metric-comparison">{{ comparisonPeriod }}</div>
    <div v-if="state === 'loaded' && freshness" class="metric-freshness">Updated {{ freshness }}</div>
  </div>
</template>

<script>
export default {
  name: 'MetricCard',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], default: null },
    unit: { type: String, default: '' },
    trendDirection: { type: String, default: '' },
    trendLabel: { type: String, default: '' },
    comparisonPeriod: { type: String, default: '' },
    freshness: { type: String, default: '' },
    state: { type: String, default: 'loaded' },
    clickAction: { type: Function, default: null }
  },
  computed: {
    displayValue() {
      if (this.value === null || this.value === undefined) return '--';
      return typeof this.value === 'number' ? this.value.toLocaleString('en-GB') : this.value;
    },
    trendArrow() {
      if (this.trendDirection === 'up') return '\u2191';
      if (this.trendDirection === 'down') return '\u2193';
      if (this.trendDirection === 'neutral') return '\u2192';
      return '';
    },
    ariaLabel() {
      var parts = [this.label, this.displayValue];
      if (this.unit) parts.push(this.unit);
      if (this.trendLabel) parts.push(this.trendLabel);
      if (this.freshness) parts.push('Updated ' + this.freshness);
      if (this.clickAction) parts.push('Click for details');
      return parts.join(', ');
    }
  },
  methods: {
    handleClick() {
      if (this.clickAction) this.clickAction();
    }
  }
};
</script>

<style scoped>
.metric-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.metric-card-clickable {
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;
}
.metric-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #3b82f6;
}
.metric-card-clickable:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.metric-skeleton {
  color: #cbd5e1;
  animation: pulse 1.5s ease-in-out infinite;
}

.metric-unavailable {
  font-size: 0.875rem;
  color: #94a3b8;
  font-weight: 500;
}

.metric-error {
  font-size: 0.875rem;
  color: #ef4444;
  font-weight: 500;
}

.metric-empty {
  color: #cbd5e1;
}

.metric-unit {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 500;
}

.metric-trend {
  font-size: 0.8125rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.trend-up { color: #16a34a; }
.trend-down { color: #dc2626; }
.trend-neutral { color: #64748b; }

.trend-arrow {
  font-size: 1rem;
}

.metric-comparison {
  font-size: 0.75rem;
  color: #94a3b8;
}

.metric-freshness {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 4px;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
